import { DropdownMenu, IconMenuItemContent } from "@zapier/product-nav";
import { useCallback, useEffect } from "react";
import { useReactFlow, useViewport } from "@xyflow/react";
import styles from "./ZoomSelect.module.css";
import { useAtomValue, useSetAtom } from "jotai";
import {
  autoLayoutSetter,
  createNotificationSetter,
  graphDirectionAtom,
  isViewOnlyModeAtom,
  nodesAtom,
  saveCanvasSetter,
} from "@zapier/canvas-state";
import { MAX_ZOOM, MIN_ZOOM, ZOOM_LEVELS } from "@zapier/canvas-constants";

import { AutoLayoutSuccessMessage } from "../AutoLayoutSuccessMessage";
import getNextZoomOutLevel from "../../utils/getNextZoomOutLevel";
import getNextZoomInLevel from "../../utils/getNextZoomInLevel";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";

function toPercentage(decimal: number) {
  return `${Math.floor(decimal * 100)}%`;
}

function ZoomSelect() {
  const setGraphDirection = useSetAtom(graphDirectionAtom);
  const setAutoLayout = useSetAtom(autoLayoutSetter);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const createNotification = useSetAtom(createNotificationSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const nodes = useAtomValue(nodesAtom);
  const { emitEventOnAutoLayout } = useEmitUserInteractionEvents();

  const runAutoLayoutAndSave = useCallback(async () => {
    const { nodes, isSuccess } = setAutoLayout();
    emitEventOnAutoLayout({ forAllSteps: true });

    if (isSuccess) {
      saveToBackend({ nodes });

      createNotification({
        type: "success",
        message: <AutoLayoutSuccessMessage />,
      });
    }
  }, [setAutoLayout, createNotification, saveToBackend, emitEventOnAutoLayout]);

  const { zoom } = useViewport();
  const { fitView, zoomTo } = useReactFlow();

  useEffect(() => {
    function handleKeyPress(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey) {
        if (["Equal", "Minus", "Digit0"].includes(event.code)) {
          switch (event.code) {
            case "Equal": // Cmd/Ctrl + "+" for zoom in
              event.preventDefault(); // Prevent default browser zoom behavior
              zoomTo(getNextZoomInLevel(zoom, ZOOM_LEVELS));
              break;
            case "Minus": // Cmd/Ctrl + "-" for zoom out
              event.preventDefault(); // Prevent default browser zoom behavior
              zoomTo(getNextZoomOutLevel(zoom, ZOOM_LEVELS));
              break;
            case "Digit0": // Cmd/Ctrl + "0" for zoom to 100%
              zoomTo(1);
              break;
          }
        }
      }

      // Option (or Alt on Windows) + 1 for zoom to fit
      if (event.altKey && event.code === "Digit1") {
        fitView();
      }
    }

    window.addEventListener("keydown", handleKeyPress);

    // Clean up the event listener
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [fitView, zoom, zoomTo]);

  const handleZoom = (value: number) => {
    zoomTo(value);
  };

  const handleZoomOut = () => {
    const nextZoom = getNextZoomOutLevel(zoom, ZOOM_LEVELS);
    zoomTo(nextZoom);
  };

  const handleZoomIn = () => {
    const nextZoom = getNextZoomInLevel(zoom, ZOOM_LEVELS);
    zoomTo(nextZoom);
  };

  const zoomMenuItems = [
    {
      title: "Zoom in",
      action: handleZoomIn,
      id: "zoom-in",
      icons: ["⌘", "+"],
      isDisabled: zoom === MAX_ZOOM,
    },
    {
      title: "Zoom out",
      action: handleZoomOut,
      id: "zoom-out",
      icons: ["⌘", "-"],
      isDisabled: zoom === MIN_ZOOM,
    },
    {
      title: "Zoom to fit",
      action: fitView,
      id: "zoom-to-fit",
      icons: ["⌥", "1"],
    },
    {
      title: "Zoom to 50%",
      action: () => handleZoom(0.5),
      id: "zoom-to-50%",
      isDisabled: zoom === 0.5,
    },
    {
      title: "Zoom to 100%",
      action: () => handleZoom(1),
      id: "zoom-to-100%",
      icons: ["⌘", "0"],
      isDisabled: zoom === 1,
    },
    {
      title: "Zoom to 200%",
      action: () => handleZoom(2),
      id: "zoom-to-200%",
      isDisabled: zoom === 2,
    },
  ];

  const otherMenuItems = [
    {
      title: "Auto layout (left to right)",
      action: () => {
        setGraphDirection("LR");
        runAutoLayoutAndSave();
      },
      id: "auto-layout-lr",
      icons: ["⌘", "⇧", "Y"],
      isDisabled: isViewOnlyMode || nodes.length === 0,
    },
    {
      title: "Auto layout (top to bottom)",
      action: () => {
        setGraphDirection("TB");
        runAutoLayoutAndSave();
      },
      id: "auto-layout-tb",
      isDisabled: isViewOnlyMode || nodes.length === 0,
    },
  ];

  const allItems = [...zoomMenuItems, ...otherMenuItems];

  const disabledKeys = allItems.reduce((acc, item) => {
    if (item.isDisabled) {
      acc.push(item.id);
    }
    return acc;
  }, [] as string[]);

  const actions = {
    ...allItems.reduce((obj, item) => ({ ...obj, [item.id]: item.action }), {}),
  };

  const handleAction = (key: React.Key) => {
    const action = actions[key as keyof typeof actions];
    if (action) {
      if (typeof action === "function") {
        (action as typeof Function)();
      }
    }
  };

  return (
    <div className={styles.wrapper}>
      <DropdownMenu
        label={toPercentage(zoom)}
        iconAfter="arrowSmallDown"
        onAction={handleAction}
        disabledKeys={disabledKeys}
      >
        <DropdownMenu.Section label="Zoom actions">
          {zoomMenuItems.map((item) => (
            <DropdownMenu.Item key={item.id} id={item.id}>
              <IconMenuItemContent textValue={item.title} icons={item.icons} />
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Section>
        <DropdownMenu.Separator />
        <DropdownMenu.Section label="Other actions">
          {otherMenuItems.map((item) => (
            <DropdownMenu.Item key={item.id} id={item.id}>
              <IconMenuItemContent textValue={item.title} icons={item.icons} />
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Section>
      </DropdownMenu>
    </div>
  );
}

export default ZoomSelect;
